import React, { useState, useEffect } from 'react';
import { fetchFeedbackFollowUp } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];

export default function FeedbackFollowUp({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchFeedbackFollowUp({
      StartDate: toApiDate(start),
      EndDate: toApiDate(end),
    })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.orders ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;
  const sentCount = rows.filter(r => r.feedbackEmailSent).length;
  const pendingCount = rows.filter(r => !r.feedbackEmailSent).length;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Feedback Email Follow Up</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={load} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Total Orders', value: total.toLocaleString() },
            { label: 'Email Sent', value: sentCount.toLocaleString(), color: '#1a8a4a' },
            { label: 'Pending', value: pendingCount.toLocaleString(), color: pendingCount > 0 ? '#b06a00' : 'var(--navy)' },
          ].map(({ label, value, color }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: color ?? 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Orders {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No orders found.</div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Method</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Delivery Date</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Email Sent</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--navy)', fontWeight: 600 }}>{toTitleCase(row.name ?? row.customerName ?? row.billTo ?? '—')}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-500)', fontSize: 11 }}>{row.email ?? row.customerEmail ?? '—'}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.shippingMethod ?? row.shipType ?? '—'}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>
                      {row.deliveryDate ? new Date(row.deliveryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {row.feedbackEmailSent
                        ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#e6f9f0', color: '#1a8a4a' }}>Sent</span>
                        : <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#fff8e6', color: '#b06a00' }}>Pending</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
